import Card from '@mui/material/Card';
import Box from '@mui/material/Box';
import IconButton from '@mui/material/IconButton';
import Typography from '@mui/material/Typography';
import { alpha } from '@mui/material/styles';
import clsx from 'clsx';
import FuseSvgIcon from '@fuse/core/FuseSvgIcon';
import NavLinkAdapter from '@fuse/core/NavLinkAdapter';
import formatDistanceToNow from 'date-fns/formatDistanceToNow';

const getClickableSx = (isDefaultVariant) => (theme) => {
  const accent = theme.palette.secondary.main;
  const restingShadow = `0 1px 3px 0 ${alpha(theme.palette.common.black, 0.2)}, inset 0 0 0 1px ${alpha(
    accent,
    0
  )}, inset 0 0 0 500px ${alpha(accent, 0)}`;
  const hoverShadow = `0 10px 24px -6px ${alpha(theme.palette.common.black, 0.45)}, inset 0 0 0 1px ${alpha(
    accent,
    0.6
  )}, inset 0 0 0 500px ${alpha(accent, isDefaultVariant ? 0.08 : 0)}`;

  return {
    cursor: 'pointer',
    boxShadow: restingShadow,
    transition: theme.transitions.create(['transform', 'box-shadow'], {
      duration: 220,
      easing: theme.transitions.easing.easeInOut,
    }),
    '&:hover, &:focus-visible': {
      transform: 'translateY(-2px)',
      boxShadow: hoverShadow,
      outline: 'none',
      '& .notification-icon': {
        backgroundColor: accent,
        color: theme.palette.secondary.contrastText,
        transform: 'scale(1.08)',
      },
      ...(isDefaultVariant && { '& .notification-title': { color: accent } }),
    },
    '&:active': { transform: 'translateY(0)' },
    '@media (prefers-reduced-motion: reduce)': {
      transition: 'none',
      '&:hover, &:focus-visible': { transform: 'none' },
    },
  };
};

function NotificationCard(props) {
  const { item, className } = props;
  const variant = item?.variant || '';
  const isClickable = Boolean(item.link);
  const isDefaultVariant = !variant || variant === 'default';

  const handleClose = (ev) => {
    ev.preventDefault();
    ev.stopPropagation();

    if (props.onClose) {
      props.onClose(item.id);
    }
  };

  return (
    <Card
      className={clsx(
        'flex items-center relative w-full rounded-16 p-20 min-h-64 space-x-8',
        !isClickable && 'shadow',
        variant === 'success' && 'bg-green-600 text-white',
        variant === 'info' && 'bg-blue-700 text-white',
        variant === 'error' && 'bg-red-600 text-white',
        variant === 'warning' && 'bg-orange-600 text-white',
        className
      )}
      elevation={0}
      sx={isClickable ? getClickableSx(isDefaultVariant) : undefined}
      component={item.useRouter ? NavLinkAdapter : 'div'}
      to={item.link || ''}
      role={item.link && 'button'}
    >
      {item.icon && !item.image && (
        <Box
          sx={{
            backgroundColor: 'background.default',
            transition: 'background-color 220ms ease, color 220ms ease, transform 220ms ease',
          }}
          className="notification-icon flex shrink-0 items-center justify-center w-32 h-32 mr-12 rounded-full"
        >
          <FuseSvgIcon className="opacity-75" color="inherit">
            {item.icon}
          </FuseSvgIcon>
        </Box>
      )}

      {item.image && (
        <img
          className="shrink-0 w-32 h-32 mr-12 rounded-full overflow-hidden object-cover object-center"
          src={item.image}
          alt="Notification"
        />
      )}

      <div className="flex flex-col flex-auto">
        {item.title && (
          <Typography
            className="notification-title font-semibold line-clamp-1"
            sx={{ transition: 'color 220ms ease' }}
          >
            {item.title}
          </Typography>
        )}

        {item.description && (
          <div className="line-clamp-2" dangerouslySetInnerHTML={{ __html: item.description }} />
        )}

        {item.item && (
          <Typography className="mt-8 text-sm leading-none " color="text.secondary">
            {formatDistanceToNow(new Date(item.time), { addSuffix: true })}
          </Typography>
        )}
      </div>

      {props.onClose && (
        <IconButton
          disableRipple
          className="top-0 right-0 absolute p-8"
          color="inherit"
          size="small"
          onClick={handleClose}
        >
          <FuseSvgIcon size={12} className="opacity-75" color="inherit">
            heroicons-solid:x
          </FuseSvgIcon>
        </IconButton>
      )}
      {item.children}
    </Card>
  );
}

export default NotificationCard;
